import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchCustomers, createCustomer } from '../api/admin';
import { Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';

export function CustomersPage() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    // Form States
    const [newCompanyName, setNewCompanyName] = useState('');
    const [newContactPerson, setNewContactPerson] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [newPhone, setNewPhone] = useState('');
    const [newNotes, setNewNotes] = useState('');

    // Snackbar Toast State
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const showMessage = (msg: string) => {
        setSnackbarMessage(msg);
        setSnackbarOpen(true);
    };

    const resetCreateForm = () => {
        setNewCompanyName('');
        setNewContactPerson('');
        setNewEmail('');
        setNewPhone('');
        setNewNotes('');
    };

    const handleCloseCreateDialog = () => {
        if (createMutation.isPending) return; // Prevent close during submit
        setCreateDialogOpen(false);
        resetCreateForm();
    };

    const validateEmailFormat = (emailStr: string) => {
        if (!emailStr) return true;
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(emailStr);
    };

    const getValidationError = () => {
        if (!newCompanyName.trim()) {
            return 'Company name is required';
        }
        if (newEmail.trim() && !validateEmailFormat(newEmail.trim())) {
            return 'Invalid email format';
        }
        return null;
    };

    const { data, isLoading } = useQuery({
        queryKey: ['customers'],
        queryFn: fetchCustomers,
    });

    const createMutation = useMutation({
        mutationFn: createCustomer,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['customers'] });
            showMessage('Customer created successfully');
            setCreateDialogOpen(false);
            resetCreateForm(); // Clean form only on success
        },
        onError: (err: any) => {
            showMessage(err.response?.data?.error || 'Failed to create customer');
        }
    });

    const handleCreateCustomer = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        
        const valError = getValidationError();
        if (valError) {
            showMessage(valError);
            return;
        }

        createMutation.mutate({
            companyName: newCompanyName.trim(),
            contactPerson: newContactPerson.trim(),
            email: newEmail.trim(),
            phone: newPhone.trim(),
            notes: newNotes.trim(),
        });
    };

    if (isLoading) {
        return <div className="text-center py-8 text-gray-500">Loading customers...</div>;
    }

    const customers = data?.customers || [];

    // Client-side search filtering (includes name, person, email, phone, notes)
    const filteredCustomers = customers.filter((customer: any) => {
        const term = searchTerm.toLowerCase();
        return (
            (customer.companyName || '').toLowerCase().includes(term) ||
            (customer.contactPerson || '').toLowerCase().includes(term) ||
            (customer.email || '').toLowerCase().includes(term) ||
            (customer.phone || '').toLowerCase().includes(term) ||
            (customer.notes || '').toLowerCase().includes(term)
        );
    });

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Customers
                </h1>
                <button
                    onClick={() => {
                        resetCreateForm();
                        setCreateDialogOpen(true);
                    }}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium transition-colors"
                >
                    Create Customer
                </button>
            </div>

            {/* Search Filter */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150/40 dark:border-gray-700/60 p-4 mb-6">
                <div className="max-w-md">
                    <input
                        placeholder="Search customers..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    />
                </div>
            </div>

            {/* Create Customer Dialog */}
            <Dialog
                open={createDialogOpen}
                onClose={handleCloseCreateDialog}
                PaperProps={{ className: 'w-[480px] max-w-full !rounded-3xl dark:bg-gray-800 dark:text-white p-2' }}
            >
                <DialogTitle className="!font-bold !text-lg text-gray-900 dark:text-white">Create Customer</DialogTitle>
                <form onSubmit={handleCreateCustomer}>
                    <DialogContent className="space-y-4 !py-2">
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                Company Name
                            </label>
                            <input
                                type="text"
                                value={newCompanyName}
                                onChange={(e) => setNewCompanyName(e.target.value)}
                                required
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                            {!newCompanyName.trim() && (
                                <p className="text-[10px] text-red-500 font-semibold mt-1 font-sans">Company name is required</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                Contact Person
                            </label>
                            <input
                                type="text"
                                value={newContactPerson}
                                onChange={(e) => setNewContactPerson(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                Email
                            </label>
                            <input
                                type="email"
                                value={newEmail}
                                onChange={(e) => setNewEmail(e.target.value)}
                                required
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            />
                            {newEmail.trim() && !validateEmailFormat(newEmail.trim()) && (
                                <p className="text-[10px] text-red-500 font-semibold mt-1 font-sans">Invalid email format</p>
                            )}
                        </div>
                        <div className="grid grid-cols-1 gap-4">
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                    Phone
                                </label>
                                <input
                                    type="text"
                                    value={newPhone}
                                    onChange={(e) => setNewPhone(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">
                                    Notes
                                </label>
                                <textarea
                                    value={newNotes}
                                    onChange={(e) => setNewNotes(e.target.value)}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                                />
                            </div>
                        </div>
                    </DialogContent>
                    <DialogActions className="!px-6 !pb-4">
                        <Button
                            onClick={handleCloseCreateDialog}
                            disabled={createMutation.isPending}
                            className="!text-gray-500 hover:!bg-slate-100"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={createMutation.isPending || !!getValidationError()}
                            className="!rounded-lg !px-4"
                        >
                            {createMutation.isPending ? 'Creating...' : 'Create Customer'}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* List */}
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-sm border border-gray-150/40 dark:border-gray-700/60">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Customer List
                    </h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead>
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Company Name
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Contact Person
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Email
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">
                                    Licenses
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {filteredCustomers.length === 0 ? (
                                <tr>
                                    <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400 italic">
                                        No matching customers found
                                    </td>
                                </tr>
                            ) : (
                                filteredCustomers.map((customer: any) => (
                                    <tr 
                                        key={customer.id}
                                        onClick={() => navigate(`/customers/${customer.id}`)}
                                        className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-all animate-none"
                                    >
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap font-semibold">
                                            {customer.companyName}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap">
                                            {customer.contactPerson || '-'}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap font-mono text-gray-600 dark:text-gray-400">
                                            {customer.email}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-200 whitespace-nowrap font-semibold">
                                            {customer.assignedLicenses?.length || 0}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Snackbar */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </div>
    );
}
export default CustomersPage;